import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Audio,
  staticFile,
  Sequence,
} from "remotion";

export const KyuStoreMonolithBentoFull: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Subtle camera drift
  const subtleScale = interpolate(frame, [0, durationInFrames], [1, 1.03], {
    extrapolateRight: "clamp",
  });

  // Entrance springs
  const hookSpring = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const bento1Spring = spring({
    frame: frame - 120,
    fps,
    config: { damping: 15, stiffness: 150 },
  });

  const bento2Spring = spring({
    frame: frame - 250,
    fps,
    config: { damping: 15, stiffness: 150 },
  });

  const ctaSpring = spring({
    frame: frame - 380,
    fps,
    config: { damping: 14, stiffness: 160 },
  });

  // Dynamic glow pulse
  const glowPulse = interpolate(
    Math.sin(frame / 15),
    [-1, 1],
    [0.06, 0.14]
  );

  // Subtitle timing based on VO
  let subtitleText = "";
  if (frame >= 5 && frame < 90) {
    subtitleText = "Trik paling simpel biar pengeluaran top up game lu tetap aman...";
  } else if (frame >= 90 && frame < 165) {
    subtitleText = "Jangan pernah checkout di luar dua tanggal ini!";
  } else if (frame >= 165 && frame < 270) {
    subtitleText = "Kyu Store selalu rilis Flash Sale rutin di pertengahan bulan...";
  } else if (frame >= 270 && frame < 370) {
    subtitleText = "...dan di akhir bulan spesial momen gajian.";
  } else if (frame >= 370 && frame < 490) {
    subtitleText = "Pasang reminder di kalender HP lu sekarang di kyustore.com!";
  }

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070908",
        color: "#ffffff",
        fontFamily: "'Inter', -apple-system, sans-serif",
        boxSizing: "border-box",
        transform: `scale(${subtleScale})`,
      }}
    >
      {/* ─── AUDIO ENGINE ────────────────────────────────────────────── */}
      <Audio
        src={staticFile("audio/vo_flashsale_bento_mastered.mp3")}
        volume={1.0}
      />

      <Audio
        src={staticFile("bgm-clean.mp3")}
        volume={(f) =>
          interpolate(
            f,
            [0, 15, durationInFrames - 30, durationInFrames],
            [0, 0.12, 0.12, 0],
            { extrapolateRight: "clamp" }
          )
        }
      />

      <Sequence from={8} durationInFrames={30}>
        <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />
      </Sequence>

      <Sequence from={120} durationInFrames={30}>
        <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />
      </Sequence>

      <Sequence from={250} durationInFrames={40}>
        <Audio src={staticFile("chime.mp3")} volume={0.7} />
      </Sequence>

      <Sequence from={380} durationInFrames={30}>
        <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />
      </Sequence>

      {/* ─── BACKGROUND FX ───────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "750px",
          height: "750px",
          background: `radial-gradient(circle, rgba(232,243,110,${glowPulse}) 0%, rgba(7,9,8,0) 70%)`,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* ─── MASTER TIKTOK SAFE ZONE CONTAINER ────────────────────────── */}
      {/* Top: 220px (bypasses FYP tabs & status bar) */}
      {/* Left: 80px, Right: 140px (bypasses right-side Action Rail icons: like/share/comment) */}
      {/* Bottom: 480px (bypasses caption, username, audio disc, and bottom nav) */}
      <div
        style={{
          position: "absolute",
          top: "220px",
          left: "80px",
          right: "140px",
          bottom: "470px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        {/* HEADER SECTION */}
        <div
          style={{
            transform: `translateY(${interpolate(hookSpring, [0, 1], [-20, 0])}px)`,
            opacity: hookSpring,
          }}
        >
          {/* Status Pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              backgroundColor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
              padding: "8px 18px",
              borderRadius: "100px",
              marginBottom: "18px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#e8f36e",
                boxShadow: "0 0 12px #e8f36e",
              }}
            />
            <div
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#d0dbd4",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              KYU STORE • FLASH SALE ROUTINE
            </div>
          </div>

          <div
            style={{
              fontSize: "72px",
              fontWeight: 800,
              lineHeight: 1.02,
              letterSpacing: "-0.04em",
              color: "#ffffff",
            }}
          >
            Top Up Cerdas,
          </div>
          <div
            style={{
              fontSize: "72px",
              fontWeight: 800,
              lineHeight: 1.02,
              letterSpacing: "-0.04em",
              color: "#e8f36e",
            }}
          >
            Tunggu Tanggalnya.
          </div>
          <div
            style={{
              fontSize: "20px",
              color: "#84968d",
              marginTop: "12px",
              lineHeight: 1.35,
              fontWeight: 500,
            }}
          >
            Flash Sale resmi rutin 2 kali sebulan. Jangan checkout di luar jadwal ini.
          </div>
        </div>

        {/* BENTO CARDS SECTION */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          {/* Bento Card 1: Mid Month */}
          <div
            style={{
              backgroundColor: "rgba(20, 26, 23, 0.85)",
              border:
                frame >= 120 && frame < 250
                  ? "2px solid #e8f36e"
                  : "1px solid rgba(232,243,110, 0.25)",
              borderRadius: "20px",
              padding: "24px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `translateY(${interpolate(bento1Spring, [0, 1], [30, 0])}px) scale(${
                frame >= 120 && frame < 250 ? 1.02 : 1
              })`,
              opacity: bento1Spring,
              backdropFilter: "blur(20px)",
              boxShadow:
                frame >= 120 && frame < 250
                  ? "0 10px 30px rgba(232,243,110,0.15)"
                  : "none",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#e8f36e",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                JADWAL 01 // PERIODE PERTAMA
              </div>
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: 800,
                  color: "#ffffff",
                  letterSpacing: "-0.02em",
                }}
              >
                Pertengahan Bulan
              </div>
              <div
                style={{
                  fontSize: "16px",
                  color: "#97a89f",
                  marginTop: "4px",
                  fontWeight: 500,
                }}
              >
                Refill pass mingguan game
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#e8f36e",
                color: "#070908",
                fontWeight: 900,
                fontSize: "20px",
                padding: "12px 22px",
                borderRadius: "14px",
                boxShadow: "0 4px 14px rgba(232,243,110,0.3)",
              }}
            >
              TGL 15
            </div>
          </div>

          {/* Bento Card 2: Payday */}
          <div
            style={{
              backgroundColor: "rgba(20, 26, 23, 0.85)",
              border:
                frame >= 250
                  ? "2px solid #ffffff"
                  : "1px solid rgba(255,255,255, 0.12)",
              borderRadius: "20px",
              padding: "24px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `translateY(${interpolate(bento2Spring, [0, 1], [30, 0])}px) scale(${
                frame >= 250 ? 1.02 : 1
              })`,
              opacity: bento2Spring,
              backdropFilter: "blur(20px)",
              boxShadow:
                frame >= 250
                  ? "0 10px 30px rgba(255,255,255,0.12)"
                  : "none",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#ffffff",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                JADWAL 02 // PERIODE KEDUA
              </div>
              <div
                style={{
                  fontSize: "34px",
                  fontWeight: 800,
                  color: "#ffffff",
                  letterSpacing: "-0.02em",
                }}
              >
                Akhir Bulan (Payday)
              </div>
              <div
                style={{
                  fontSize: "16px",
                  color: "#97a89f",
                  marginTop: "4px",
                  fontWeight: 500,
                }}
              >
                Spesial momen gajian
              </div>
            </div>

            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1.5px solid rgba(255,255,255,0.3)",
                color: "#ffffff",
                fontWeight: 900,
                fontSize: "18px",
                padding: "12px 20px",
                borderRadius: "14px",
              }}
            >
              PAYDAY
            </div>
          </div>
        </div>

        {/* CTA STRIP (SAFELY INSIDE SAFE BOX) */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            paddingTop: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transform: `translateY(${interpolate(ctaSpring, [0, 1], [15, 0])}px)`,
            opacity: ctaSpring,
          }}
        >
          <div style={{ fontSize: "16px", color: "#84968d", fontWeight: 500 }}>
            Simpan tanggalnya. Serbu pas harinya.
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: 800,
              color: "#e8f36e",
              letterSpacing: "0.02em",
            }}
          >
            kyustore.com ➔
          </div>
        </div>
      </div>

      {/* ─── DYNAMIC SUBTITLE BAND (ANCHORED AT GOLDEN Y: 1460-1510PX) ── */}
      {/* bottom: 410px ensures it floats right above the TikTok caption & username */}
      <div
        style={{
          position: "absolute",
          bottom: "400px",
          left: "80px",
          right: "140px",
          zIndex: 30,
          textAlign: "center",
          minHeight: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {subtitleText && (
          <div
            style={{
              backgroundColor: "rgba(10, 14, 12, 0.94)",
              border: "1px solid rgba(232,243,110, 0.45)",
              borderRadius: "14px",
              padding: "10px 22px",
              color: "#ffffff",
              fontSize: "21px",
              fontWeight: 700,
              lineHeight: 1.3,
              boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
              backdropFilter: "blur(12px)",
            }}
          >
            {subtitleText}
          </div>
        )}
      </div>

      {/* Area Y: 1520px - 1920px (bawah 400px) bersih 100% dari teks, aman dari tab/caption TikTok */}
    </AbsoluteFill>
  );
};
